<template>
    <md-dialog :md-active.sync="showDialogEditGrupo" :md-click-outside-to-close="false" class="group-dialog">
        <md-dialog-title class="dialog-header">
            <span class="header-title">{{ labels.editGroup }}</span>
        </md-dialog-title>

        <md-dialog-content class="dialog-content">
            <div class="form-container">
                <!-- Lista de grupos para seleccionar -->
                <b-row>
                    <b-col md="12">
                        <div class="form-group-custom">
                            <label class="label-custom">{{ labels.selectGroup }} <span class="required">*</span></label>
                            <md-field class="custom-field">
                                <md-select 
                                    v-model="selectedGroupId" 
                                    :placeholder="labels.selectGroup"
                                    @md-selected="onGroupSelected">
                                    <md-option 
                                        v-for="grupo in gruposList" 
                                        :key="grupo.id_grupos" 
                                        :value="grupo.id_grupos">
                                        {{ grupo.nombre }}
                                    </md-option>
                                </md-select>
                            </md-field>
                        </div>
                    </b-col>
                </b-row>

                <!-- Formulario de edición (se muestra solo cuando hay un grupo seleccionado) -->
                <div v-if="selectedGroupId && editItem.id">
                    <b-row>
                        <b-col md="12">
                            <div class="form-group-custom">
                                <label class="label-custom">{{ labels.name }} <span class="required">*</span></label>
                                <md-field class="custom-field" :class="{ 'md-invalid': errors.nombre }">
                                    <md-input
                                        type="text"
                                        name="name"
                                        v-model="editItem.nombre"
                                        :placeholder="labels.name"
                                        @blur="validateField('nombre')"
                                    />
                                    <span class="error-message" v-if="errors.nombre">{{ errors.nombre }}</span>
                                </md-field>
                            </div>
                        </b-col>
                    </b-row>
                </div>
            </div>
        </md-dialog-content>

        <md-dialog-actions class="dialog-actions">
            <div class="actions-left">
                <md-button class="md-raised delete-btn" @click="confirmDelete" :disabled="!selectedGroupId">
                    <b-icon icon="trash" class="me-2"></b-icon>
                    {{ labels.delete }}
                </md-button>
            </div>
            <div class="actions-right">
                <md-button class="md-raised cancel-btn" @click="clicked">
                    {{ labels.close }}
                </md-button>
                <md-button class="md-raised md-primary save-btn" @click="editGrupo" :disabled="!selectedGroupId">
                    {{ labels.save }}
                </md-button>
            </div>
        </md-dialog-actions>
    </md-dialog>
</template>

<script>
    import Grupo from '@/services/Grupo'

    export default {
        name: "EditGrupo",
        props: ['showDialogEditGrupo'],

        data: () => ({
            gruposList: [],
            selectedGroupId: null,
            editItem: {
                id: null,
                nombre: ''
            },
            errors: {
                nombre: ""
            }
        }),

        watch: {
            showDialogEditGrupo(val) {
                if (val) {
                    this.cargarGrupos();
                    this.resetSeleccion();
                }
            }
        },

        computed: {
            labels() {
                if (this.$store.state._language == "es-ES") {
                    return {
                        editGroup: "Editar Grupo",
                        selectGroup: "Seleccionar Grupo",
                        name: "Nombre",
                        save: "Guardar",
                        close: "Cerrar",
                        delete: "Eliminar"
                    }
                } else {
                    return {
                        editGroup: "Edit Group",
                        selectGroup: "Select Group",
                        name: "Name",
                        save: "Save",
                        close: "Close",
                        delete: "Delete"
                    }
                }
            }
        },

        methods: {
            async cargarGrupos() {
                try {
                    const result = await Grupo.getAllGrupos();
                    this.gruposList = result.data.grupos || [];
                    
                    if (this.gruposList.length === 0) {
                        this.$toast.warning(this.$store.state._language == "es-ES" 
                            ? "No hay grupos disponibles para editar" 
                            : "No groups available to edit");
                    }
                } catch (error) {
                    console.error("Error loading groups:", error);
                    this.$toast.error(this.$store.state._language == "es-ES" 
                        ? "Error al cargar los grupos" 
                        : "Error loading groups");
                }
            },

            resetSeleccion() {
                this.selectedGroupId = null;
                this.editItem = { id: null, nombre: '' };
                this.resetErrors();
            },

            onGroupSelected(groupId) {
                const selectedGroup = this.gruposList.find(g => g.id_grupos === groupId);
                if (selectedGroup) {
                    this.editItem = {
                        id: selectedGroup.id_grupos,
                        nombre: selectedGroup.nombre
                    };
                    this.resetErrors();
                }
            },

            resetErrors() {
                this.errors = { nombre: "" };
            },

            validateField(field) {
                const lang = this.$store.state._language;
                
                if (field === 'nombre') {
                    if (!this.editItem.nombre) {
                        this.errors.nombre = lang == "es-ES" 
                            ? "El nombre del grupo es obligatorio" 
                            : "Group name is required";
                    } else if (this.editItem.nombre.length < 3) {
                        this.errors.nombre = lang == "es-ES" 
                            ? "El nombre debe tener al menos 3 caracteres" 
                            : "Name must have at least 3 characters";
                    } else if (this.editItem.nombre.length > 255) {
                        this.errors.nombre = lang == "es-ES" 
                            ? "El nombre no puede exceder los 255 caracteres" 
                            : "Name cannot exceed 255 characters";
                    } else {
                        this.errors.nombre = "";
                    }
                }
            },

            validateForm() {
                this.validateField('nombre');
                return !this.errors.nombre;
            },

            confirmDelete() {
                const message = this.$store.state._language == "es-ES"
                    ? `¿Está seguro de que desea eliminar el grupo "${this.editItem.nombre}"?`
                    : `Are you sure you want to delete the group "${this.editItem.nombre}"?`;
                
                if (window.confirm(message)) {
                    this.deleteGrupo();
                }
            },

            async deleteGrupo() {
                try {
                    const res = await Grupo.delete(this.editItem.id);
                    
                    this.$toast.success(res.data.message || (this.$store.state._language == "es-ES" 
                        ? "Grupo eliminado exitosamente" 
                        : "Group deleted successfully"));
                    
                    // Recargar la lista de grupos
                    await this.cargarGrupos();
                    
                    // Limpiar la selección
                    this.resetSeleccion();
                    
                    // Notificar al componente padre que se actualizó un grupo
                    this.$emit('grupoActualizado');
                    
                } catch (error) {
                    console.error("Error deleting group:", error);
                    
                    let errorMessage = this.$store.state._language == "es-ES" 
                        ? "Error al eliminar el grupo" 
                        : "Error deleting group";
                    
                    if (error.response && error.response.data) {
                        if (error.response.data.message) {
                            errorMessage = error.response.data.message;
                        } else if (error.response.data.error) {
                            errorMessage = error.response.data.error;
                        }
                    }
                    
                    this.$toast.error(errorMessage);
                }
            },

            async editGrupo() {
                if (!this.selectedGroupId) {
                    this.$toast.error(this.$store.state._language == "es-ES" 
                        ? "Por favor, seleccione un grupo" 
                        : "Please select a group");
                    return;
                }

                if (!this.validateForm()) {
                    this.$toast.error(this.$store.state._language == "es-ES" 
                        ? "Por favor, corrija los errores del formulario" 
                        : "Please correct the form errors");
                    return;
                }

                try {
                    const grupoData = {
                        nombre: this.editItem.nombre
                    };
                    
                    const res = await Grupo.update(this.editItem.id, grupoData);
                    
                    this.$toast.success(res.data.message || (this.$store.state._language == "es-ES" 
                        ? "Grupo actualizado exitosamente" 
                        : "Group updated successfully"));
                    
                    // Recargar la lista de grupos
                    await this.cargarGrupos();
                    
                    // Limpiar la selección
                    this.resetSeleccion();
                    
                    this.$emit('grupoActualizado');
                    
                } catch (error) {
                    console.error("Error updating group:", error);
                    
                    let errorMessage = this.$store.state._language == "es-ES" 
                        ? "Error al actualizar el grupo" 
                        : "Error updating group";
                    
                    if (error.response && error.response.data) {
                        if (error.response.data.message) {
                            errorMessage = error.response.data.message;
                        } else if (error.response.data.error) {
                            errorMessage = error.response.data.error;
                        }
                    }
                    
                    this.$toast.error(errorMessage);
                }
            },

            clicked() {
                this.resetSeleccion();
                this.$emit('clicked', false);
            }
        }
    }
</script>

<style scoped>
.group-dialog >>> .md-dialog {
    max-width: 500px;
    width: 90%;
    border-radius: 8px;
    overflow: hidden;
}

.dialog-header {
    background: #34495e;
    color: white;
    padding: 20px 24px;
    margin: 0;
    border-bottom: 1px solid #2c3e50;
}

.header-title {
    font-size: 1.3rem;
    font-weight: 500;
}

.dialog-content {
    padding: 20px 24px;
    max-height: 70vh;
    overflow-y: auto;
}

.form-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.form-group-custom {
    margin-bottom: 0;
}

.label-custom {
    display: block;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.required {
    color: #e74c3c;
}

.custom-field {
    margin: 0;
}

.md-field.md-invalid .md-input {
    border-color: #ff5252;
}

.error-message {
    color: #ff5252;
    font-size: 12px;
    margin-top: 4px;
    display: block;
}

.dialog-actions {
    padding: 20px 24px;
    border-top: 1px solid #ecf0f1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f8f9fa;
}

.actions-left {
    display: flex;
}

.actions-right {
    display: flex;
    gap: 12px;
}

.delete-btn {
    background: #e74c3c !important;
    color: white !important;
    font-size: 14px;
    padding: 10px 20px;
    min-width: 120px;
}

.delete-btn:hover {
    background: #c0392b !important;
}

.delete-btn:disabled {
    background: #bdc3c7 !important;
    cursor: not-allowed;
    opacity: 0.6;
}

.cancel-btn {
    background: #95a5a6 !important;
    color: white !important;
    font-size: 14px;
    padding: 10px 24px;
    min-width: 100px;
}

.save-btn {
    background: #3498db !important;
    color: white !important;
    font-size: 14px;
    padding: 10px 24px;
    min-width: 100px;
}

.cancel-btn:hover {
    background: #7f8c8d !important;
}

.save-btn:hover {
    background: #2980b9 !important;
}

.save-btn:disabled {
    background: #bdc3c7 !important;
    cursor: not-allowed;
    opacity: 0.6;
}

/* Scroll personalizado */
.dialog-content::-webkit-scrollbar {
    width: 8px;
}

.dialog-content::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.dialog-content::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
}

.dialog-content::-webkit-scrollbar-thumb:hover {
    background: #555;
}

/* Responsive */
@media (max-width: 768px) {
    .group-dialog >>> .md-dialog {
        width: 95%;
        margin: 16px;
    }
    
    .dialog-actions {
        flex-direction: column;
        gap: 12px;
    }
    
    .actions-left,
    .actions-right {
        width: 100%;
        justify-content: center;
    }
    
    .delete-btn, .cancel-btn, .save-btn {
        width: 100%;
        margin: 4px 0;
    }
}
</style>